import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { css } from 'styled-components'

import { Button, ResetIcon } from '@blade/ui-kit'
import { FilterButton, FilterPopover } from '@root/shared/ui-kit'
import { Input } from '@root/shared/ui-kit'

import type {
  StringFilter,
  TableFilter,
} from '@root/shared/ui-kit/data-table/atoms/table-filter'

export interface FilterByNameProps<T extends string> {
  filter: TableFilter<string> | undefined
  setColumnFilter: (filter: TableFilter<T>) => void
  clearColumnFilter: () => void
  columnId: T
}

export const FilterByName = <T extends string>({
  filter,
  setColumnFilter,
  clearColumnFilter,
  columnId,
}: FilterByNameProps<T>) => {
  if (filter && filter.type !== 'string') {
    throw new Error('FilterByName: filter must be a string filter')
  }

  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(filter?.value)

  useEffect(() => {
    setValue(filter?.value)
  }, [filter])

  const handleApply = () => {
    if (!value) {
      clearColumnFilter()
      setIsOpen(false)
      return
    }
    const filter: StringFilter<T> = {
      type: 'string',
      column: columnId,
      operator: 'eq',
      value,
    }
    setColumnFilter(filter)
    setIsOpen(false)
  }

  const handleReset = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    clearColumnFilter()
    setValue('')
    setIsOpen(false)
  }
  const hasFilter = filter !== undefined

  const trigger = (
    <FilterByMakerTrigger>
      <FilterButton active={isOpen || hasFilter} />
      {t('common.name')}
    </FilterByMakerTrigger>
  )

  return (
    <FilterPopover
      trigger={trigger}
      title={t('common.name')}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <FilterByMakerContent
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleApply()
        }}
      >
        <FilterByMakerInputLabel>{t('common.name')}</FilterByMakerInputLabel>

        <Input
          size="m"
          placeholder={t('common.name')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          css={css`
            --input-height: 37px;
          `}
        />
        <FilterByMakerFooter>
          <FilterResetButton
            variant="ghost"
            radius="rounded"
            size="xs"
            onClick={handleReset}
            disabled={!value}
          >
            <ResetIcon />
            {t('trade.filters.common.reset')}
          </FilterResetButton>
          <Button
            variant="solid"
            color="primary"
            radius="rounded"
            size="xs"
            onClick={handleApply}
            disabled={!value}
          >
            {t('trade.filters.common.apply')}
          </Button>
        </FilterByMakerFooter>
      </FilterByMakerContent>
    </FilterPopover>
  )
}

const FilterByMakerTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--text-11);

  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--text-12);
  }
`

const FilterByMakerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FilterByMakerContent = styled.form`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 6px;
`

const FilterByMakerInputLabel = styled.p`
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  color: var(--text-11);
`

const FilterResetButton = styled(Button)`
  gap: 4px;
  padding-left: 0px;
  font-weight: 400;
  &:hover {
    background: transparent;
  }
`
